"use client";

import { createContext, useContext, type ReactNode } from "react";

/**
 * The console layout's page-header extras, delivered to whatever renders the
 * page title.
 *
 * Console is the one layout with no header bar on desktop — the detail pane
 * fills the width, as the prototype draws it
 * (`showHeader = config.layout !== "console" || narrow`). That leaves the
 * studio's status and the actions StudioHeader carries — Customise, New post,
 * library, notifications, the profile card — with nowhere to live above `md`.
 *
 * The prototype's answer is `ConsoleBar`: one row at the top of the detail
 * pane holding the screen title, the studio's status pills, and those actions.
 * Our pages already render their own title row, so rather than stacking a
 * second bar above it, the console layout hands its extras down and the
 * existing title row renders them inline. That is what makes it ONE bar,
 * matching the prototype, instead of two.
 *
 * Every other layout leaves this context at its default of `null`, so both
 * consumers render nothing and no other layout moves by a pixel. That default
 * is the whole safety story here: this cannot leak into Standard, Reader or
 * Spaces, because none of them provides it.
 */
interface ConsolePaneHeader {
  /** Status pills, rendered beside the page title. */
  badges: ReactNode;
  /** Actions, rendered hard right in the title row. */
  actions: ReactNode;
}

const ConsolePaneHeaderContext = createContext<ConsolePaneHeader | null>(null);

export function ConsolePaneHeaderProvider({
  value,
  children,
}: {
  value: ConsolePaneHeader;
  children: ReactNode;
}) {
  return (
    <ConsolePaneHeaderContext.Provider value={value}>
      {children}
    </ConsolePaneHeaderContext.Provider>
  );
}

/**
 * True only inside the console layout's detail pane.
 *
 * Used for the one thing that is a layout decision rather than a slot: the
 * feed's title row pins itself while the pane scrolls under it, which is how
 * the prototype's ConsoleBar behaves (it sits outside the scrolling `main`).
 * Pages built on components/layouts/page-layout.tsx already pin their header
 * in every layout; the feed page is the one that did not.
 */
export function useIsConsolePane(): boolean {
  return useContext(ConsolePaneHeaderContext) !== null;
}

/** Status pills for the console pane, or nothing in every other layout. */
export function ConsolePaneBadges() {
  return <>{useContext(ConsolePaneHeaderContext)?.badges ?? null}</>;
}

/** Header actions for the console pane, or nothing in every other layout. */
export function ConsolePaneActions() {
  return <>{useContext(ConsolePaneHeaderContext)?.actions ?? null}</>;
}
